import type { ExpandedState } from '@tanstack/react-table';
import type { HeaderConfig, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { addMonths, format } from 'date-fns';
import { dequal } from 'dequal';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toISOTimestamp } from 'src/lib/utils/common/dateFormats';
import { FullSizeSpinner } from '@/components/ui/FullSizeSpinner';
import type { Item } from '@/lib/api/types/items';
import type {
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
  IVisualizationHeader,
} from '@/lib/api/types/roadmapping';
import type { Team } from '@/lib/api/types/teams';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { useUpdateRoadmapItemMutation } from '@/lib/mutations/roadmapping/items/useUpdateRoadmapItemMutation';
import { useUpsertTimelineLayoutMutation } from '@/lib/mutations/roadmapping/useTimelineLayoutMutations';
import {
  useTimelineHeaderConfig,
  useTimelineSubHeaderConfigs,
} from '@/lib/queries/roadmapping/useTimelineHeaderConfig';
import { useVisualizationItemRanks } from '@/lib/queries/roadmapping/useVisualizationItemRanks';
import {
  DEFAULT_FALLBACK_COLOR,
  DEFAULT_PALETTE_ID,
  generateItemColorMap,
  getPaletteSwatches,
} from '@/lib/utils/roadmapping/colors';
import { headerToPropertySelector } from '@/lib/utils/roadmapping/commonLayoutUtils';
import { getItemFieldValue } from '@/lib/utils/roadmapping/fieldExtraction';
import { VisualizationNoHeadersState } from '../empty-state/VisualizationNoHeadersState';
import { VisualizationNoItemsMatchFiltersState } from '../empty-state/VisualizationNoItemsMatchFiltersState';
import { VisualizationNoItemsMatchLayoutState } from '../empty-state/VisualizationNoItemsMatchLayoutState';
import { VisualizationNoItemsState } from '../empty-state/VisualizationNoItemsState';
import { Legend } from '../published/Legend';
import { COMPOSITE_ID_SEPARATOR, NO_VALUE_ID } from '../utils/constants';
import { useRowHeaderWidth } from '../utils/useRowHeaderWidth';
import { useVisualizationHeaderOptions } from '../utils/useVisualizationHeaderOptions';
import { getHeaderFieldId, getHeaderItemKey } from '../utils/visualizationHeaderUtils';
import { useViewContext } from '../ViewContext';
import { DependencyProvider } from './dependencies/DependencyContext';
import { TimelineMinimap } from './minimap/TimelineMinimap';
import { ReorderTimelineHeadersDialog } from './ReorderTimelineHeadersDialog';
import { TimelineColorLegendPopover } from './TimelineColorLegendPopover';
import { TimelineNoItemsWithDates } from './TimelineNoItemsWithDates';
import { TimelineTable } from './TimelineTable';
import { useFilteredTimelineItems } from './useFilteredTimelineItems';
import { useTimelineColorContext } from './useTimelineColorContext';
import { useTimelineMainHeaders, useTimelineSubHeaders } from './useTimelineHeaderColors';
import { useTimelineItemLevels } from './useTimelineItemLevels';
import { useTimelineItemTypeFilter } from './useTimelineItemTypeFilter';
import { useTimelineMinimapDates } from './useTimelineMinimapDates';
import type { TimelineRowType } from './useTimelineTableColumns';
import { useTimelineTableData } from './useTimelineTableData';

export interface TimelineProps {
  items: Item[];
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  layout: TimelineLayoutDTO;
  visualizationId: number;
  hasFilters: boolean;
  from: string | null;
  to: string | null;
  onDateChange: (dates: { from: string; to: string }) => void;
  canEdit: boolean;
  viewLocked?: boolean;
  includeDependencies: boolean;
  isPublishedMode?: boolean;
  publishedLegendItems?: { color: string; label: string }[];
  mainHeaderConfig?: HeaderConfig;
  subHeaderConfigs?: Record<string, HeaderConfig>;
  teamsOverride?: Team[];
  onItemClick?: (itemId: number) => void;
}

export const Timeline = ({
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  layout,
  visualizationId,
  hasFilters,
  from,
  to,
  onDateChange,
  canEdit,
  viewLocked: _viewLocked = false,
  includeDependencies,
  isPublishedMode = false,
  publishedLegendItems,
  mainHeaderConfig: mainHeaderConfigProp,
  subHeaderConfigs: subHeaderConfigsProp,
  teamsOverride,
  onItemClick,
}: TimelineProps) => {
  const { t } = useTranslation();
  const integrationItemsEditable = useFlag('roadmapsIntegrationItemsEditable');
  const { viewId } = useViewContext();
  const [expandedRows, setExpandedRows] = useState<ExpandedState>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [expandedChildren, setExpandedChildren] = useState<Record<string, boolean>>({});
  const [reorderHeadersModalState, setReorderHeadersModalState] = useState<{
    isReorderingMainHeader: boolean;
    parentHeaderId: string | null;
  } | null>(null);

  const { mutate: upsertLayout } = useUpsertTimelineLayoutMutation(viewId, visualizationId);
  const { mutate: updateRoadmapItem } = useUpdateRoadmapItemMutation();
  const { data: itemRanks } = useVisualizationItemRanks({
    viewId,
    visualizationId,
    enabled: !isPublishedMode,
  });

  const { rowHeaderWidth, setRowHeaderWidth } = useRowHeaderWidth({
    layoutWidth: layout.rowHeaderWidth,
    defaultWidth: 120,
    mutate: upsertLayout,
  });

  const { itemTypeFilter } = useTimelineItemTypeFilter(layout.itemLevelId, !isPublishedMode);

  useEffect(() => {
    if (from !== null && to !== null) {
      return;
    }
    const today = new Date();
    const initialFrom = layout.startDate ?? format(today, 'yyyy-MM-dd');
    const initialTo = layout.endDate ?? format(addMonths(today, 6), 'yyyy-MM-dd');
    onDateChange({ from: initialFrom, to: initialTo });
  }, [layout, from, to, onDateChange]);

  const { curLevelItemTypes, nextLevelItemTypes } = useTimelineItemLevels({
    itemLevelId: layout.itemLevelId,
    itemTypes,
    itemLevels,
  });

  const filteredItemsWithChildren = useFilteredTimelineItems({
    from,
    to,
    items,
    curLevelItemTypes,
    nextLevelItemTypes,
    showChildItems: layout.showChildItems ?? false,
    integrationItemsEditable,
  });

  const itemsData = useMemo(
    () => ({
      items: filteredItemsWithChildren,
      itemTypes,
      itemLevels,
      itemStatuses,
    }),
    [filteredItemsWithChildren, itemTypes, itemLevels, itemStatuses],
  );

  const timelineHeader = useMemo(
    () => headerToPropertySelector(layout.header1 ?? null),
    [layout.header1],
  );
  const timelineSubHeader = useMemo(
    () => headerToPropertySelector(layout.header2 ?? null),
    [layout.header2],
  );

  const {
    currentColorByField: contextColorByField,
    orderedHeaderValues: contextOrderedHeaderValues,
  } = useTimelineColorContext({
    items,
    itemTypes,
    itemLevels,
    itemStatuses,
    noValueLabel: t('roadmapping.layout.formatPanel.colors.noValue'),
    isPublishedMode,
    publishedLayout: isPublishedMode ? layout : undefined,
  });

  const handleDependencyCreate = useCallback(
    ({
      sourceItemId,
      dependencyType,
      dependencyItemId,
    }: {
      sourceItemId: number;
      dependencyType: 'PREDECESSOR' | 'SUCCESSOR';
      dependencyItemId: number;
    }) => {
      // Will implement logic later if we add deps back to project scope
      // biome-ignore lint/suspicious/noConsole: Logging for development
      console.log({ sourceItemId, dependencyType, dependencyItemId });
    },
    [],
  );

  const timelineHeaderOptions = useVisualizationHeaderOptions(
    timelineHeader,
    items,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
    },
  ) as IVisualizationHeader;

  const timelineSubHeaderOptions = useVisualizationHeaderOptions(
    timelineSubHeader,
    items,
    layout.itemLevelId || 0,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      teamsOverride,
      enabled: !isPublishedMode,
    },
  ) as IVisualizationHeader;

  const showEmptyHeaders = layout.showEmptyHeaders ?? false;

  const header1FieldId = useMemo(
    () => getHeaderFieldId(timelineHeaderOptions),
    [timelineHeaderOptions],
  );
  const header2FieldId = useMemo(
    () => getHeaderFieldId(timelineSubHeaderOptions),
    [timelineSubHeaderOptions],
  );

  const includeNullParentItemHeader = useCallback(
    (headerOptions: IVisualizationHeader | null) => {
      if (headerOptions?.type !== 'ParentItemSelector') return false;
      if (showEmptyHeaders) return true;

      const parentIds = new Set(
        headerOptions.options
          .map((option) => option.id)
          .filter((id): id is number => typeof id === 'number'),
      );
      return items.some((item) => item.parentId == null || !parentIds.has(item.parentId));
    },
    [items, showEmptyHeaders],
  );

  const includeNullHeader1 = includeNullParentItemHeader(timelineHeaderOptions);
  const includeNullHeader2 = includeNullParentItemHeader(timelineSubHeaderOptions);

  const effectiveColorByField = contextColorByField || 'status';
  const orderedHeaderValues = contextOrderedHeaderValues;

  const mainHeaders = useTimelineMainHeaders({
    timelineHeaderOptions,
    colorConfig: layout.colorConfig,
    header1FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader: includeNullHeader1,
  });

  const subHeaders = useTimelineSubHeaders({
    timelineSubHeaderOptions,
    colorConfig: layout.colorConfig,
    header2FieldId,
    effectiveColorByField,
    orderedHeaderValues,
    includeNullHeader: includeNullHeader2,
  });

  const itemColorById = useMemo(() => {
    const colorConfig = layout.colorConfig;
    if (!colorConfig || colorConfig.mode !== 'palette') return undefined;
    if (!effectiveColorByField) return undefined;

    const paletteSwatches = getPaletteSwatches({
      mode: 'palette',
      paletteId: colorConfig.paletteId ?? DEFAULT_PALETTE_ID,
    });

    let orderedHeaders: Array<{ id: string }> | undefined;
    if (colorConfig.headerColorOrder && colorConfig.headerColorOrder.length > 0) {
      orderedHeaders = colorConfig.headerColorOrder.map((id) => ({ id }));
    } else if (
      effectiveColorByField === header1FieldId ||
      effectiveColorByField === header2FieldId
    ) {
      orderedHeaders = orderedHeaderValues.map((id) => ({ id }));
      if (orderedHeaders.length === 0 && effectiveColorByField === header1FieldId) {
        orderedHeaders = mainHeaders
          .map((h) => ({ id: h.id?.toString() ?? '' }))
          .filter((h) => h.id.length > 0);
      }
    }

    const colorIndexByValue = generateItemColorMap(items, effectiveColorByField, orderedHeaders);

    const result: Record<number, string> = {};
    for (const item of items) {
      const fieldValue = getItemFieldValue(item, effectiveColorByField);
      const resolvedValue = fieldValue ?? NO_VALUE_ID;
      const index = colorIndexByValue[resolvedValue];
      result[item.id] =
        index === undefined
          ? DEFAULT_FALLBACK_COLOR
          : paletteSwatches.itemColors[index % paletteSwatches.itemColors.length] ||
            DEFAULT_FALLBACK_COLOR;
    }
    return result;
  }, [
    items,
    mainHeaders,
    layout.colorConfig,
    header1FieldId,
    header2FieldId,
    effectiveColorByField,
    orderedHeaderValues,
  ]);

  const paletteLegendItems = useMemo(() => {
    if (!isPublishedMode || !itemColorById) return undefined;

    const seenFieldValues = new Set<string>();
    const result: { color: string; label: string }[] = [];

    for (const item of items) {
      const fieldValue = getItemFieldValue(item, effectiveColorByField);
      if (fieldValue == null) continue;
      if (seenFieldValues.has(fieldValue)) continue;
      seenFieldValues.add(fieldValue);

      const color = itemColorById[item.id];
      if (!color || color === DEFAULT_FALLBACK_COLOR) continue;

      const numericFieldValue = Number(fieldValue);
      let label: string;
      if (effectiveColorByField === 'status') {
        label = itemStatuses.find((s) => s.id === numericFieldValue)?.name ?? fieldValue;
      } else if (effectiveColorByField === 'team') {
        label = teamsOverride?.find((t) => t.id === numericFieldValue)?.name ?? fieldValue;
      } else {
        label = itemTypes.find((t) => t.id === numericFieldValue)?.name ?? fieldValue;
      }

      result.push({ color, label });
    }

    return result.length > 0 ? result : undefined;
  }, [
    isPublishedMode,
    itemColorById,
    items,
    effectiveColorByField,
    itemStatuses,
    teamsOverride,
    itemTypes,
  ]);

  const { data: mainHeaderConfigQuery } = useTimelineHeaderConfig({
    viewId,
    visualizationId,
    headerFieldId: isPublishedMode ? null : header1FieldId,
  });

  const subHeaderConfigsQuery = useTimelineSubHeaderConfigs({
    viewId,
    visualizationId,
    headerFieldId: header2FieldId,
    parentFieldId: header1FieldId,
    headers: isPublishedMode ? [] : mainHeaders,
  });

  const mainHeaderConfig = isPublishedMode ? mainHeaderConfigProp : mainHeaderConfigQuery;
  const subHeaderConfigs = isPublishedMode ? subHeaderConfigsProp : subHeaderConfigsQuery;

  const handleItemDrop = useCallback(
    (
      itemId: number,
      row: TimelineRowType,
      newStartDate: string,
      newEndDate: string,
      sourceRow?: TimelineRowType,
    ) => {
      const headerFieldId = getHeaderFieldId(timelineHeaderOptions);
      const itemHeaderPropertyKey = getHeaderItemKey(headerFieldId);
      const itemHeaderValue = row.parentHeaderId ? row.parentHeaderId : row.headerId;
      const sourceHeaderValue = sourceRow
        ? sourceRow.parentHeaderId
          ? sourceRow.parentHeaderId
          : sourceRow.headerId
        : undefined;

      const subHeaderFieldId = getHeaderFieldId(timelineSubHeaderOptions);
      const itemSubHeaderPropertyKey = getHeaderItemKey(subHeaderFieldId);
      const itemSubHeaderValue = row.parentHeaderId ? row.headerId : null;
      const sourceSubHeaderValue = sourceRow?.parentHeaderId ? sourceRow.headerId : null;

      let customFieldData:
        | { customFieldId: string; value: string | number | boolean | null }[]
        | null = null;
      let multiSelectFieldData:
        | {
            customFieldId: string;
            values: (string | number | boolean)[];
            previousEntryIds: number[];
          }[]
        | null = null;
      const updatedItemValues: Partial<Item> & Record<string, string | number | null> = {};

      const combineMultiSelect = layout.combineMultiSelectHeaders ?? false;
      const item = items.find((i) => i.id === itemId);

      const parseValue = (v: string): string | number => {
        const num = Number(v);
        return Number.isNaN(num) ? v : num;
      };

      const handleHeaderValue = (
        propertyKey: string,
        targetValue: string,
        sourceValue: string | null | undefined,
      ) => {
        if (propertyKey.startsWith('customFields.')) {
          const customFieldKey = propertyKey.split('.')[1] as string;
          const currentFieldValue = item?.customFields?.[customFieldKey];
          const isMultiSelect = Array.isArray(currentFieldValue);

          if (isMultiSelect) {
            const previousEntryIds = (currentFieldValue as unknown[])
              .filter((v): v is { id: number } => typeof v === 'object' && v !== null && 'id' in v)
              .map((v) => v.id);

            if (targetValue === NO_VALUE_ID) {
              multiSelectFieldData = [
                ...(multiSelectFieldData ?? []),
                { customFieldId: customFieldKey, values: [], previousEntryIds },
              ];
            } else if (combineMultiSelect) {
              const values = targetValue.includes(COMPOSITE_ID_SEPARATOR)
                ? targetValue.split(COMPOSITE_ID_SEPARATOR).map(parseValue)
                : [parseValue(targetValue)];
              multiSelectFieldData = [
                ...(multiSelectFieldData ?? []),
                { customFieldId: customFieldKey, values, previousEntryIds },
              ];
            } else {
              const sourceNum = Number(sourceValue);
              const targetNum = Number(targetValue);
              const currentValues = (currentFieldValue as unknown[]).map((v) =>
                typeof v === 'object' && v !== null && 'value' in v
                  ? (v as { value: number }).value
                  : (v as number),
              );
              const newValues = sourceValue
                ? currentValues.filter((v: number) => v !== sourceNum).concat(targetNum)
                : currentValues.concat(targetNum);
              multiSelectFieldData = [
                ...(multiSelectFieldData ?? []),
                { customFieldId: customFieldKey, values: newValues, previousEntryIds },
              ];
            }
          } else {
            const parsedCustomValue = targetValue === NO_VALUE_ID ? null : parseValue(targetValue);
            customFieldData = [
              ...(customFieldData ?? []),
              { customFieldId: customFieldKey, value: parsedCustomValue },
            ];
          }
        } else {
          const parsedValue =
            targetValue !== '' && !Number.isNaN(Number(targetValue))
              ? Number(targetValue)
              : targetValue;
          (updatedItemValues as Record<string, string | number | null>)[propertyKey] =
            parsedValue === NO_VALUE_ID ? null : parsedValue;
        }
      };

      if (itemHeaderPropertyKey && itemHeaderValue !== undefined) {
        handleHeaderValue(itemHeaderPropertyKey, itemHeaderValue, sourceHeaderValue);
        if (itemSubHeaderPropertyKey && itemSubHeaderValue !== null) {
          handleHeaderValue(itemSubHeaderPropertyKey, itemSubHeaderValue, sourceSubHeaderValue);
        }
      }

      if (integrationItemsEditable) {
        updatedItemValues.targetStartDateTime = toISOTimestamp(newStartDate);
        updatedItemValues.targetFinishDateTime = toISOTimestamp(newEndDate);
      }

      updateRoadmapItem({
        itemId,
        itemData: updatedItemValues,
        customFieldData,
        multiSelectFieldData,
      });
    },
    [
      items,
      timelineHeaderOptions,
      timelineSubHeaderOptions,
      updateRoadmapItem,
      integrationItemsEditable,
      layout.combineMultiSelectHeaders,
    ],
  );

  const handleItemResize = useCallback(
    (itemId: number, newStartDate: string, newEndDate: string) => {
      if (!integrationItemsEditable) {
        return;
      }
      updateRoadmapItem({
        itemId,
        itemData: {
          targetStartDateTime: toISOTimestamp(newStartDate),
          targetFinishDateTime: toISOTimestamp(newEndDate),
        },
        customFieldData: null,
        multiSelectFieldData: null,
      });
    },
    [integrationItemsEditable, updateRoadmapItem],
  );

  const {
    deferredFrom,
    deferredTo,
    firstItemWeekStartDate,
    lastItemWeekEndDate,
    minimapStart,
    minimapEnd,
    deferredMinimapStart,
    deferredMinimapEnd,
  } = useTimelineMinimapDates({ from, to, items: itemsData.items });

  const deferredLayout = useDeferredValue(layout);

  const data = useTimelineTableData({
    from: deferredFrom || '',
    to: deferredTo || '',
    minimapStart: deferredMinimapStart,
    minimapEnd: deferredMinimapEnd,
    mainHeaders,
    subHeaders,
    items: filteredItemsWithChildren,
    timelineHeaderOptions,
    timelineSubHeaderOptions,
    layout,
    expandedChildren,
    mainHeaderConfig,
    subHeaderConfigs,
    itemRanks,
  });

  const deferredData = useDeferredValue(data);

  const hasNoItems = items.length === 0 && !hasFilters;
  const noItemsMatchFilters = items.length === 0 && hasFilters;
  const hasNoItemsMatchingLayout = items.length === 0 && Object.keys(itemTypeFilter).length > 0;
  const hasNoHeaders = !timelineHeader;
  const hasItemsButNoDates =
    items.length > 0 && firstItemWeekStartDate === null && lastItemWeekEndDate === null;
  const isUpdatingLayout = !dequal(layout, deferredLayout);

  if (!deferredFrom || !deferredTo || !from || !to) return <FullSizeSpinner />;
  if (hasNoItems) return <VisualizationNoItemsState visualizationType="timeline" />;
  if (hasNoHeaders) return <VisualizationNoHeadersState visualizationType="timeline" />;
  if (noItemsMatchFilters) return <VisualizationNoItemsMatchFiltersState />;
  if (hasNoItemsMatchingLayout) return <VisualizationNoItemsMatchLayoutState />;
  if (hasItemsButNoDates) return <TimelineNoItemsWithDates />;

  return (
    <DependencyProvider
      includeDependencies={canEdit && includeDependencies}
      items={items}
      onDependencyCreate={handleDependencyCreate}
      isCompact={layout.theme === 'compact'}
    >
      <div className="flex flex-col max-w-full max-h-full relative">
        {!isPublishedMode && (
          <TimelineMinimap
            visualizationId={visualizationId}
            viewId={viewId}
            from={deferredFrom}
            to={deferredTo}
            data={data}
            onChange={onDateChange}
            minimapStart={minimapStart}
            minimapEnd={minimapEnd}
            canEdit={canEdit}
            scrollContainerRef={scrollContainerRef}
            expandedRows={expandedRows}
            itemColorById={itemColorById}
            isCompact={layout.theme === 'compact'}
          />
        )}

        {isUpdatingLayout && (
          <div className="absolute inset-0 bg-bg-surface-base/70 dark:bg-bg-neutral-bold/70 z-10 flex items-center justify-center">
            <FullSizeSpinner />
          </div>
        )}
        <TimelineTable
          from={deferredFrom}
          to={deferredTo}
          data={deferredData}
          canEdit={canEdit}
          isPublishedMode={isPublishedMode}
          onItemDrop={handleItemDrop}
          onItemResize={handleItemResize}
          header1FieldId={header1FieldId}
          header2FieldId={header2FieldId}
          scrollContainerRef={scrollContainerRef}
          layout={layout}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          itemColorById={itemColorById}
          rowHeaderWidth={rowHeaderWidth}
          onRowHeaderWidthChange={setRowHeaderWidth}
          expandedChildren={expandedChildren}
          setExpandedChildren={setExpandedChildren}
          setReorderHeadersModalState={setReorderHeadersModalState}
          onItemClick={onItemClick}
          viewId={viewId}
          visualizationId={visualizationId}
          itemRanks={itemRanks}
        />

        {isPublishedMode ? (
          (paletteLegendItems ?? publishedLegendItems)?.length ? (
            <div className="absolute bottom-4 right-4 z-10">
              <Legend items={paletteLegendItems ?? publishedLegendItems ?? []} />
            </div>
          ) : null
        ) : (
          <div className="absolute bottom-4 right-4 z-10">
            <TimelineColorLegendPopover
              items={itemsData.items}
              itemTypes={itemsData.itemTypes}
              itemLevels={itemsData.itemLevels}
              itemStatuses={itemsData.itemStatuses}
            />
          </div>
        )}
      </div>

      {reorderHeadersModalState && (
        <ReorderTimelineHeadersDialog
          isOpen={true}
          onClose={() => setReorderHeadersModalState(null)}
          headers={reorderHeadersModalState.isReorderingMainHeader ? mainHeaders : subHeaders}
          headerFieldId={
            reorderHeadersModalState.isReorderingMainHeader ? header1FieldId : header2FieldId
          }
          parentHeaderId={reorderHeadersModalState.parentHeaderId}
          parentFieldId={!reorderHeadersModalState.isReorderingMainHeader ? header1FieldId : null}
          visualizationId={visualizationId}
        />
      )}
    </DependencyProvider>
  );
};
